/**
 * Dashboard query key factory and hooks with caching strategy
 *
 * Cache times:
 * - Student dashboard: 5 minutes (invalidated on quiz submission)
 * - Teacher dashboard: 15 minutes (org-wide data, can be stale)
 */

import type {
  GetStudentDashboardData,
  GetTeacherDashboardData,
  GetTeacherFlashcardDashboardData,
} from "@/services/dashboardService"
import { DashboardService } from "@/services/dashboardService"
import type {
  FlashcardCardStat,
  StudentDashboardResponse,
  SubjectPercentileHistoryResponse,
  TeacherDashboardResponse,
  TeacherFlashcardDashboardResponse,
} from "@/types/dashboard"
import { useQuery } from "@tanstack/react-query"

// Cache time constants (in milliseconds)
export const CACHE_TIMES = {
  STUDENT_DASHBOARD: 5 * 60 * 1000, // 5 minutes
  TEACHER_DASHBOARD: 15 * 60 * 1000, // 15 minutes
  ATTEMPT_HISTORY: 10 * 60 * 1000, // 10 minutes
  ANALYTICS: 30 * 60 * 1000, // 30 minutes
} as const

// Query key factory for type-safe cache keys
export const dashboardKeys = {
  all: ["dashboard"] as const,
  student: (filters?: GetStudentDashboardData) =>
    [...dashboardKeys.all, "student", filters] as const,
  teacher: (filters?: GetTeacherDashboardData) =>
    [...dashboardKeys.all, "teacher", filters] as const,
} as const

/**
 * Hook to fetch student dashboard data with caching
 */
export function useStudentDashboard(filters?: GetStudentDashboardData) {
  return useQuery<StudentDashboardResponse>({
    queryKey: dashboardKeys.student(filters),
    queryFn: () => DashboardService.getStudentDashboard(filters || {}),
    staleTime: CACHE_TIMES.STUDENT_DASHBOARD,
  })
}

/**
 * Hook to fetch teacher dashboard data with caching
 */
export function useTeacherDashboard(filters?: GetTeacherDashboardData) {
  return useQuery<TeacherDashboardResponse>({
    queryKey: dashboardKeys.teacher(filters),
    queryFn: () => DashboardService.getTeacherDashboard(filters || {}),
    staleTime: CACHE_TIMES.TEACHER_DASHBOARD,
  })
}

export function useSubjectPercentileHistory(subjectId: string | null) {
  return useQuery<SubjectPercentileHistoryResponse>({
    queryKey: ["subject-percentile-history", subjectId],
    queryFn: () =>
      DashboardService.getSubjectPercentileHistory({ subjectId: subjectId! }),
    enabled: subjectId !== null,
    staleTime: CACHE_TIMES.STUDENT_DASHBOARD,
  })
}

/**
 * Hook to fetch teacher-dashboard flashcard analytics.
 * `opts.enabled` is forwarded so callers can lazy-load on tab activation.
 */
export function useTeacherFlashcardDashboard({
  enabled,
  programIds,
}: GetTeacherFlashcardDashboardData & { enabled: boolean }) {
  return useQuery<TeacherFlashcardDashboardResponse>({
    queryKey: [...dashboardKeys.all, "teacher-flashcards", programIds] as const,
    queryFn: () =>
      DashboardService.getTeacherFlashcardDashboard({ programIds }),
    enabled,
    staleTime: CACHE_TIMES.TEACHER_DASHBOARD,
  })
}

/**
 * Hook to fetch card-level difficulty stats for one deck.
 * Only fires when deckId is non-null (i.e. a row is expanded).
 */
export function useFlashcardDeckCards(deckId: string | null) {
  return useQuery<FlashcardCardStat[]>({
    queryKey: [...dashboardKeys.all, "flashcard-deck-cards", deckId] as const,
    queryFn: () => DashboardService.getFlashcardDeckCards(deckId!),
    enabled: deckId !== null,
    staleTime: CACHE_TIMES.TEACHER_DASHBOARD,
  })
}
